import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Typography,
  Alert,
  CircularProgress,
  Box,
  Card,
  CardContent,
  Chip,
  IconButton,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Grid,
  InputAdornment,
  Divider
} from '@mui/material';
import { 
  Add as AddIcon, 
  Delete as DeleteIcon,
  Science as ScienceIcon
} from '@mui/icons-material';

// API Configuration
const API_BASE_URL = process.env.REACT_APP_API_URL || (process.env.NODE_ENV === 'production' 
  ? 'https://barebiologics-api-bdgjaefwc4h7d9gu.eastus2-01.azurewebsites.net/api'
  : 'http://localhost:8080/api');

const ManufactureVialsDialog = ({ open, onClose, donor, onSuccess }) => {
  const [products, setProducts] = useState([]);
  const [manufacturedProducts, setManufacturedProducts] = useState([
    { id: Date.now(), productId: '', quantity: '', unitCost: '' }
  ]);
  const [loading, setLoading] = useState(false);
  const [productsLoading, setProductsLoading] = useState(false);
  const [error, setError] = useState('');

  // Fetch EPL products when dialog opens
  useEffect(() => {
    if (open) {
      fetchProducts();
    }
  }, [open]);

  const fetchProducts = async () => {
    try {
      setProductsLoading(true);
      const token = localStorage.getItem('authToken');
      const response = await fetch(`${API_BASE_URL}/products?organization=EPL&status=ACTIVE`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        setProducts(data.data || []);
      }
    } catch (err) {
      console.error('Error fetching products:', err);
    } finally {
      setProductsLoading(false);
    }
  };

  const handleAddProduct = () => {
    setManufacturedProducts([
      ...manufacturedProducts,
      { id: Date.now(), productId: '', quantity: '', unitCost: '' }
    ]);
  };

  const handleRemoveProduct = (id) => {
    if (manufacturedProducts.length > 1) {
      setManufacturedProducts(manufacturedProducts.filter(p => p.id !== id));
    }
  };

  const handleProductChange = (id, field, value) => {
    setManufacturedProducts(manufacturedProducts.map(p => 
      p.id === id ? { ...p, [field]: value } : p
    ));
  };

  const calculateTotals = () => {
    let totalVials = 0;
    let totalCost = 0;

    manufacturedProducts.forEach(p => {
      const qty = parseInt(p.quantity) || 0;
      const cost = parseFloat(p.unitCost) || 0;
      totalVials += qty;
      totalCost += qty * cost;
    });

    return { totalVials, totalCost };
  };

  const isFormValid = manufacturedProducts.every(p => 
    p.productId && parseInt(p.quantity) > 0
  );

  const handleClose = () => {
    setManufacturedProducts([{ id: Date.now(), productId: '', quantity: '', unitCost: '' }]);
    setError('');
    onClose();
  };

  const handleSubmit = async () => {
    if (!isFormValid) {
      setError('Please fill in all required fields');
      return;
    }

    try {
      setLoading(true);
      setError('');

      const token = localStorage.getItem('authToken');
      const vials = manufacturedProducts.map(p => ({
        productId: p.productId,
        quantity: parseInt(p.quantity),
        unitCost: parseFloat(p.unitCost) || 0
      }));

      const response = await fetch(`${API_BASE_URL}/donors/${donor.id}/manufacture`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ vials })
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Failed to manufacture vials');
      }

      handleClose();
      onSuccess(data.data);
    } catch (err) {
      console.error('Error manufacturing vials:', err);
      setError(err.message || 'An error occurred');
    } finally {
      setLoading(false);
    }
  };

  if (!donor) return null;

  const { totalVials, totalCost } = calculateTotals();

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
      <DialogTitle sx={{ pb: 1 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <ScienceIcon color="primary" />
          <Typography variant="h5" fontWeight="bold">Manufacture Vials</Typography>
        </Box>
        <Typography variant="body2" color="text.secondary">
          {donor.donorId} - {donor.mareName}
        </Typography>
      </DialogTitle>
      
      <DialogContent>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {/* Donor Info Summary */}
        <Card sx={{ mb: 3, bgcolor: 'success.light', color: 'success.contrastText' }}>
          <CardContent sx={{ py: 1.5 }}>
            <Grid container spacing={2} alignItems="center">
              <Grid item xs={6}>
                <Typography variant="caption" sx={{ opacity: 0.8 }}>Donor ID</Typography>
                <Typography variant="h6" fontWeight="bold">{donor.donorId}</Typography>
              </Grid>
              <Grid item xs={6}>
                <Typography variant="caption" sx={{ opacity: 0.8 }}>Lot Number (Auto)</Typography>
                <Typography variant="h6" fontWeight="bold">{donor.donorId}</Typography>
              </Grid>
            </Grid>
          </CardContent>
        </Card>

        {/* Products to Manufacture */}
        <Typography variant="subtitle1" fontWeight="bold" sx={{ mb: 2 }}>
          Add Products Manufactured
        </Typography>

        {productsLoading ? (
          <Box sx={{ textAlign: 'center', py: 3 }}>
            <CircularProgress size={24} />
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
              Loading products...
            </Typography>
          </Box>
        ) : (
          <>
            {manufacturedProducts.map((item, index) => (
              <Card key={item.id} sx={{ mb: 2, p: 2 }}>
                <Grid container spacing={2} alignItems="center">
                  <Grid item xs={12} md={5}>
                    <FormControl fullWidth size="small">
                      <InputLabel>Product *</InputLabel>
                      <Select
                        value={item.productId}
                        onChange={(e) => handleProductChange(item.id, 'productId', e.target.value)}
                        label="Product *"
                      >
                        {products.map(product => (
                          <MenuItem key={product.id} value={product.id}>
                            {product.name} ({product.sku})
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid item xs={6} md={2}>
                    <TextField
                      label="Quantity *"
                      type="number"
                      size="small"
                      fullWidth
                      value={item.quantity}
                      onChange={(e) => handleProductChange(item.id, 'quantity', e.target.value)}
                      inputProps={{ min: 1 }}
                    />
                  </Grid>
                  <Grid item xs={6} md={3}>
                    <TextField
                      label="Unit Cost"
                      type="number"
                      size="small"
                      fullWidth
                      value={item.unitCost}
                      onChange={(e) => handleProductChange(item.id, 'unitCost', e.target.value)}
                      InputProps={{
                        startAdornment: <InputAdornment position="start">$</InputAdornment>
                      }}
                      inputProps={{ min: 0, step: 0.01 }}
                    />
                  </Grid>
                  <Grid item xs={12} md={2} sx={{ textAlign: 'center' }}>
                    <IconButton 
                      onClick={() => handleRemoveProduct(item.id)}
                      disabled={manufacturedProducts.length === 1}
                      color="error"
                    >
                      <DeleteIcon />
                    </IconButton>
                  </Grid>
                </Grid>
              </Card>
            ))}

            <Button 
              startIcon={<AddIcon />} 
              onClick={handleAddProduct}
              sx={{ mb: 3 }}
            >
              Add Another Product
            </Button>
          </>
        )}

        <Divider sx={{ my: 2 }} />

        {/* Summary */}
        <Card sx={{ bgcolor: 'grey.100' }}>
          <CardContent>
            <Typography variant="subtitle1" fontWeight="bold" sx={{ mb: 1 }}>
              Summary
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={6}>
                <Typography variant="body2" color="text.secondary">Total Vials</Typography>
                <Typography variant="h5" fontWeight="bold" color="primary">
                  {totalVials}
                </Typography>
              </Grid>
              <Grid item xs={6}>
                <Typography variant="body2" color="text.secondary">Total Cost</Typography>
                <Typography variant="h5" fontWeight="bold" color="primary">
                  ${totalCost.toFixed(2)}
                </Typography>
              </Grid>
            </Grid>
          </CardContent>
        </Card>

        <Alert severity="info" sx={{ mt: 2 }}>
          Manufactured vials will be placed in <strong>Quarantine</strong> until donor eligibility is complete.
        </Alert>
      </DialogContent>
      
      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button onClick={handleClose} disabled={loading}>
          Cancel
        </Button>
        <Button 
          onClick={handleSubmit} 
          variant="contained"
          disabled={!isFormValid || loading}
          startIcon={loading ? <CircularProgress size={20} /> : null}
          color="success"
        >
          {loading ? 'Creating...' : 'Complete Manufacturing'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ManufactureVialsDialog;

